'use client';

import Link from 'next/link';
import { ChevronRight, Home } from 'lucide-react';
import { BreadcrumbSchemaScript, type BreadcrumbItem } from '@/lib/schema';

interface BreadcrumbsProps {
  items: BreadcrumbItem[];
  className?: string;
}

export default function Breadcrumbs({ items, className = '' }: BreadcrumbsProps) {
  // Always include Home as first item
  const allItems: BreadcrumbItem[] = [
    { name: 'Home', url: '/' },
    ...items
  ];

  return (
    <>
      {/* Schema Markup */}
      <BreadcrumbSchemaScript items={allItems} />

      {/* Visual Breadcrumbs */}
      <nav
        aria-label="Breadcrumb"
        className={`flex items-center gap-2 text-sm text-gray-600 ${className}`}
      >
        <ol className="flex items-center gap-2 flex-wrap">
          {allItems.map((item, index) => {
            const isLast = index === allItems.length - 1;

            return (
              <li key={item.url} className="flex items-center gap-2">
                {index === 0 ? (
                  // Home icon for first item
                  <Link
                    href={item.url}
                    className="flex items-center gap-1 hover:text-sb-gold transition-colors"
                    aria-label="Home"
                  >
                    <Home className="w-4 h-4" />
                    <span className="sr-only">{item.name}</span>
                  </Link>
                ) : (
                  // Regular breadcrumb item
                  <>
                    {isLast ? (
                      <span
                        className="text-gray-900 font-medium"
                        aria-current="page"
                      >
                        {item.name}
                      </span>
                    ) : (
                      <Link
                        href={item.url}
                        className="hover:text-sb-gold transition-colors"
                      >
                        {item.name}
                      </Link>
                    )}
                  </>
                )}

                {/* Separator */}
                {!isLast && (
                  <ChevronRight className="w-4 h-4 text-gray-400" aria-hidden="true" />
                )}
              </li>
            );
          })}
        </ol>
      </nav>
    </>
  );
}
